import { Box, Table as ChakraTable, Flex, IconButton, Text } from '@chakra-ui/react'
import {
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'
import { FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight } from 'react-icons/fi'

/**
 * Table Component
 * TanStack Table wrapper with Chakra UI styling
 * Supports client-side and server-side sorting
 */
export function Table({
  data = [],
  columns = [],
  isLoading = false,
  enableSorting = true,
  enablePagination = true,
  enableRowSelection = false,
  manualPagination = false,
  manualSorting = false,
  pageCount: controlledPageCount,
  totalCount = 0,
  onPaginationChange,
  onSortingChange,
  onRowSelectionChange,
  state: controlledState,
  ...props
}) {
  const { t } = useTranslation()
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: enableSorting && !manualSorting ? getSortedRowModel() : undefined,
    getPaginationRowModel: enablePagination && !manualPagination ? getPaginationRowModel() : undefined,
    enableRowSelection,
    enableSorting,
    manualPagination,
    manualSorting,
    pageCount: controlledPageCount,
    onPaginationChange,
    onSortingChange,
    onRowSelectionChange,
    state: controlledState,
    ...props,
  })

  return (
    <>
      <Box border="1px solid" borderColor="#EBECF0" borderRadius="sm" overflow="hidden">
        <ChakraTable.Root variant="simple">
          <ChakraTable.Header>
            {table.getHeaderGroups().map((headerGroup) => (
              <ChakraTable.Row
                key={ headerGroup.id }
                borderBottomWidth="1px"
                borderBottomColor="#EBECF0"
              >
                {headerGroup.headers.map((header, index) => (
                  <ChakraTable.ColumnHeader
                    key={ header.id }
                    onClick={ header.column.getToggleSortingHandler() }
                    cursor={ header.column.getCanSort() ? 'pointer' : 'default' }
                    userSelect={ header.column.getCanSort() ? 'none' : 'auto' }
                    width={ header.column.columnDef.meta?.width }
                    textAlign={ header.column.id === 'select' ? 'center' : 'left' }
                    bg="#F4F5F7"
                    py={2.5}
                    px={3}
                    borderRightWidth={ index < headerGroup.headers.length - 1 ? '1px' : '0' }
                    borderRightColor="#EBECF0"
                    color="#5E6C84"
                    fontSize="11px"
                    fontWeight="700"
                    textTransform="uppercase"
                    letterSpacing="wide"
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {{ asc: ' 🔼', desc: ' 🔽' }[header.column.getIsSorted()] ?? null}
                  </ChakraTable.ColumnHeader>
                ))}
              </ChakraTable.Row>
            ))}
          </ChakraTable.Header>
          <ChakraTable.Body>
            {isLoading ? (
              <ChakraTable.Row borderBottomWidth="1px" borderBottomColor="#EBECF0">
                <ChakraTable.Cell colSpan={ columns.length } textAlign="center" py={ 8 }>
                  Loading...
                </ChakraTable.Cell>
              </ChakraTable.Row>
            ) : table.getRowModel().rows.length === 0 ? (
              <ChakraTable.Row borderBottomWidth="1px" borderBottomColor="#EBECF0">
                <ChakraTable.Cell colSpan={ columns.length } textAlign="center" py={ 8 }>
                  <Text color="text.secondary">No data available</Text>
                </ChakraTable.Cell>
              </ChakraTable.Row>
            ) : (
              table.getRowModel().rows.map((row) => (
                <ChakraTable.Row
                  key={ row.id }
                  borderBottomWidth="1px"
                  borderBottomColor="#EBECF0"
                  _hover={{ bg: '#DEEBFF' }}
                  transition="background 0.1s ease-in-out"
                >
                  {row.getVisibleCells().map((cell, index) => (
                    <ChakraTable.Cell
                      key={ cell.id }
                      width={ cell.column.columnDef.meta?.width }
                      textAlign={ cell.column.id === 'select' ? 'center' : 'left' }
                      py={1}
                      px={3}
                      borderRightWidth={ index < row.getVisibleCells().length - 1 ? '1px' : '0' }
                      borderRightColor="#EBECF0"
                      fontSize="sm"
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </ChakraTable.Cell>
                  ))}
                </ChakraTable.Row>
              ))
            )}
          </ChakraTable.Body>
        </ChakraTable.Root>
      </Box>

      {enablePagination && (
        <Flex justify="space-between" align="center" mt={ 4 } gap={ 4 } flexWrap="wrap">
          <Flex align="center" gap={ 1 }>
            <IconButton
              onClick={ () => table.setPageIndex(0) }
              disabled={ !table.getCanPreviousPage() }
              aria-label={t('pagination.firstPage')}
              size="sm"
              variant="outline"
            >
              <Box as={ FiChevronsLeft } />
            </IconButton>
            <IconButton
              onClick={ () => table.previousPage() }
              disabled={ !table.getCanPreviousPage() }
              aria-label={t('pagination.previousPage')}
              size="sm"
              variant="outline"
            >
              <Box as={ FiChevronLeft } />
            </IconButton>
            <IconButton
              onClick={ () => table.nextPage() }
              disabled={ !table.getCanNextPage() }
              aria-label={t('pagination.nextPage')}
              size="sm"
              variant="outline"
            >
              <Box as={ FiChevronRight } />
            </IconButton>
            <IconButton
              onClick={ () => table.setPageIndex(table.getPageCount() - 1) }
              disabled={ !table.getCanNextPage() }
              aria-label={t('pagination.lastPage')}
              size="sm"
              variant="outline"
            >
              <Box as={ FiChevronsRight } />
            </IconButton>
          </Flex>

          <Flex align="center" gap={ 2 }>
            <Text fontSize="sm" whiteSpace="nowrap" fontWeight="600" color="text.primary">
              {totalCount > 0 && `${t('pagination.records')} ${totalCount} - `}
              {t('pagination.page')} { table.getState().pagination.pageIndex + 1 } {t('pagination.of')} { table.getPageCount() }
            </Text>
          </Flex>

          <Flex align="center" gap={ 2 }>
            <Text fontSize="sm" whiteSpace="nowrap" color="text.secondary">
              {t('pagination.rowsPerPage')}
            </Text>
            <select
              value={ table.getState().pagination.pageSize }
              onChange={ (e) => table.setPageSize(Number(e.target.value)) }
              style={{
                padding: '0.375rem 2rem 0.375rem 0.75rem',
                borderRadius: '3px',
                border: '1px solid #DFE1E6',
                backgroundColor: '#FAFBFC',
                color: '#172B4D',
                fontSize: '0.875rem',
                fontWeight: '400',
                cursor: 'pointer',
                outline: 'none',
                appearance: 'none',
                backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'16\' height=\'16\' viewBox=\'0 0 16 16\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M4 6L8 10L12 6\' stroke=\'%235E6C84\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'/%3E%3C/svg%3E")',
                backgroundPosition: 'right 0.5rem center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '1em 1em',
                transition: 'border-color 0.1s, background-color 0.1s',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#4C9AFF'
                e.target.style.backgroundColor = '#FFFFFF'
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#DFE1E6'
                e.target.style.backgroundColor = '#FAFBFC'
              }}
            >
              {[10, 20, 30, 40, 50].map((pageSize) => (
                <option key={ pageSize } value={ pageSize }>
                  { pageSize }
                </option>
              ))}
            </select>
          </Flex>
        </Flex>
      )}
    </>
  )
}

export default Table
